import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  InputNumber,
  ColorPicker,
  Select,
  Space,
  Divider,
  Alert,
  Tooltip,
  Typography,
  Row,
  Col,
  Card,
} from 'antd';
import {
  InfoCircleOutlined,
  CreditCardOutlined,
  DollarOutlined,
  SafetyOutlined,
  BgColorsOutlined,
  ApiOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import { PaymentMethod } from '../../../types/payments';
import { useCheckCodeExistsQuery } from '../../../services/paymentMethodsApi';

const { Text } = Typography;
const { TextArea } = Input;

interface PaymentMethodFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => Promise<void>;
  paymentMethod?: PaymentMethod;
  loading?: boolean;
}

// Common payment method icons
const iconOptions = [
  { value: 'credit-card', label: 'Tarjeta de Crédito' },
  { value: 'bank', label: 'Banco' },
  { value: 'dollar', label: 'Efectivo' },
  { value: 'mobile', label: 'Pago Móvil' },
  { value: 'qrcode', label: 'Código QR' },
  { value: 'gift', label: 'Vale/Cupón' },
  { value: 'wallet', label: 'Billetera Digital' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'apple', label: 'Apple Pay' },
  { value: 'google', label: 'Google Pay' },
  { value: 'other', label: 'Otro' },
];

// Predefined colors for payment methods
const colorPresets = [
  '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', 
  '#eb2f96', '#fa541c', '#13c2c2', '#096dd9', '#389e0d',
  '#d48806', '#cf1322', '#531dab', '#c41d7f', '#d4380d',
  '#006d75', '#7cb305', '#08979c', '#ad2102', '#10239e'
];

const PaymentMethodFormModal: React.FC<PaymentMethodFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  paymentMethod,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [selectedColor, setSelectedColor] = useState('#1890ff');
  const isEditing = !!paymentMethod;

  // Watch for code changes to check uniqueness
  const codeValue = Form.useWatch('code', form);
  const { data: codeExistsData } = useCheckCodeExistsQuery(
    { 
      code: codeValue || '', 
      excludeId: isEditing ? paymentMethod?.id : undefined 
    },
    { 
      skip: !codeValue || codeValue.length < 2 
    }
  );

  useEffect(() => {
    if (visible) {
      if (isEditing && paymentMethod) {
        form.setFieldsValue({
          code: paymentMethod.code,
          name: paymentMethod.name,
          description: paymentMethod.description,
          iconName: paymentMethod.iconName,
          color: paymentMethod.color,
          isActive: paymentMethod.isActive,
          requiresReference: paymentMethod.requiresReference,
          requiresAuth: paymentMethod.requiresAuth,
          maxAmount: paymentMethod.maxAmount,
          displayOrder: paymentMethod.displayOrder,
        });
        setSelectedColor(paymentMethod.color);
      } else {
        form.resetFields();
        form.setFieldsValue({
          isActive: true,
          requiresReference: false,
          requiresAuth: false,
          color: '#1890ff',
          displayOrder: 1,
          iconName: 'credit-card',
        });
        setSelectedColor('#1890ff');
      }
    }
  }, [visible, isEditing, paymentMethod, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
      form.resetFields();
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    }
  };

  const handleColorChange = (color: any) => {
    const colorValue = typeof color === 'string' ? color : color.toHexString();
    setSelectedColor(colorValue);
    form.setFieldValue('color', colorValue);
  };

  // Validation rules
  const codeRules = [
    { required: true, message: 'El código es requerido' },
    { min: 2, max: 50, message: 'El código debe tener entre 2 y 50 caracteres' },
    { pattern: /^[a-z][a-z0-9_]*$/, message: 'Debe empezar con letra minúscula y solo contener letras minúsculas, números y guiones bajos' },
    {
      validator: async (_: any, value: string) => {
        if (value && codeExistsData?.exists) {
          throw new Error('Este código ya está en uso');
        }
      },
    },
  ];

  return (
    <Modal
      title={isEditing ? 'Editar Forma de Pago' : 'Nueva Forma de Pago'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      style={{ top: 20 }}
      okText={isEditing ? 'Actualizar' : 'Crear'}
      cancelText="Cancelar"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        style={{ marginTop: '20px' }}
      >
        <Alert
          message="Información importante"
          description="El código debe ser único, empezar con letra minúscula y solo contener letras minúsculas, números y guiones bajos (ejemplo: efectivo, tarjeta_credito). Una vez creado, el código no se puede modificar."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Row gutter={24}>
          <Col span={12}>
            <Card title={<Space><ApiOutlined />Información Básica</Space>} size="small">
              <Form.Item
                name="code"
                label={
                  <Space>
                    <span>Código</span>
                    <Tooltip title="Código único para identificar la forma de pago (ej: efectivo, tarjeta, transferencia)">
                      <InfoCircleOutlined style={{ color: 'var(--color-neutral-text)' }} />
                    </Tooltip>
                  </Space>
                }
                rules={codeRules}
                hasFeedback
              >
                <Input
                  placeholder="Ej: cash, card, transfer"
                  style={{ textTransform: 'lowercase' }}
                  maxLength={50}
                  disabled={isEditing}
                  onChange={(e) => {
                    // Convert to lowercase automatically
                    const value = e.target.value.toLowerCase();
                    form.setFieldValue('code', value);
                  }}
                />
              </Form.Item>

              <Form.Item
                name="name"
                label="Nombre"
                rules={[
                  { required: true, message: 'El nombre es requerido' },
                  { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
                ]}
              >
                <Input placeholder="Ej: Efectivo, Tarjeta de Crédito" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Descripción (opcional)"
                rules={[
                  { max: 500, message: 'La descripción no puede exceder 500 caracteres' },
                ]}
              >
                <TextArea
                  placeholder="Descripción de la forma de pago..."
                  rows={3}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col span={12}>
            <Card title={<Space><BgColorsOutlined />Apariencia</Space>} size="small">
              <Form.Item
                name="iconName"
                label="Icono"
                rules={[{ required: true, message: 'Selecciona un icono' }]}
              >
                <Select
                  placeholder="Selecciona un icono"
                  options={iconOptions}
                />
              </Form.Item>

              <Form.Item
                name="color"
                label="Color"
                rules={[{ required: true, message: 'Selecciona un color' }]}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <ColorPicker
                    value={selectedColor}
                    onChange={handleColorChange}
                    presets={[
                      {
                        label: 'Colores Recomendados',
                        colors: colorPresets,
                      },
                    ]}
                    showText
                    size="large"
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {colorPresets.slice(0, 10).map((color) => (
                      <div
                        key={color}
                        style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: color,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          border: selectedColor === color ? '2px solid #1890ff' : '1px solid var(--border-color-strong)',
                        }}
                        onClick={() => handleColorChange(color)}
                      />
                    ))}
                  </div>
                </Space>
              </Form.Item>

              <Form.Item
                name="displayOrder"
                label={
                  <Space>
                    <SortAscendingOutlined />
                    <span>Orden de Visualización</span>
                  </Space>
                }
                rules={[
                  { required: true, message: 'El orden es requerido' },
                  { type: 'number', min: 1, max: 100, message: 'El orden debe estar entre 1 y 100' },
                ]}
              >
                <InputNumber
                  placeholder="1"
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Divider>Configuración Avanzada</Divider>

        <Row gutter={24}>
          <Col span={8}>
            <Card size="small">
              <Form.Item
                name="isActive"
                label="Estado"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Activo"
                  unCheckedChildren="Inactivo"
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Solo los métodos activos aparecen en las pantallas de pago
              </Text>
            </Card>
          </Col>

          <Col span={8}>
            <Card size="small">
              <Form.Item
                name="requiresReference"
                label={
                  <Space>
                    <CreditCardOutlined />
                    <span>Requiere Referencia</span>
                  </Space>
                }
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Solicita número de referencia al procesar el pago
              </Text>
            </Card>
          </Col>

          <Col span={8}>
            <Card size="small">
              <Form.Item
                name="requiresAuth"
                label={
                  <Space>
                    <SafetyOutlined />
                    <span>Requiere Autorización</span>
                  </Space>
                }
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Requiere autorización de supervisor para usar
              </Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={24} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Form.Item
              name="maxAmount"
              label={
                <Space>
                  <DollarOutlined />
                  <span>Monto Máximo (opcional)</span>
                </Space>
              }
              rules={[
                { type: 'number', min: 0, message: 'El monto debe ser mayor a 0' },
              ]}
            >
              <InputNumber
                placeholder="Sin límite"
                formatter={(value?: string | number) =>
                  typeof value === 'number'
                    ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    : value || ''
                }
                parser={(value?: string) =>
                  Number((value || '').replace(/\$\s?|(,*)/g, ''))
                }
                style={{ width: '100%' }}
                min={0}
                precision={2}
              />
            </Form.Item>
          </Col>
        </Row>

        {!isEditing && (
          <Alert
            message="Vista previa"
            description={
              <Space>
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: selectedColor,
                    borderRadius: '4px',
                    border: '1px solid var(--border-color-strong)',
                  }}
                />
                <span>Así se verá la forma de pago en el sistema</span>
              </Space>
            }
            type="success"
            showIcon={false}
            style={{ marginTop: '16px' }}
          />
        )}
      </Form>
    </Modal>
  );
};

export default PaymentMethodFormModal;